<template>
  <vbt-dialog
    v-model="dialog"
    :title="title"
    :submit-btn-name="submitBtnName"
    width="700px"
    @submit="submitForm"
    @close="$_resetFormValidation"
  >
    <v-form :ref="formRef">
      <v-text-field
        v-if="creator"
        v-model.trim="form.name"
        :rules="validator.getRulesByField('name')"
        label="Name"
        @keypress.enter="submitForm"
      />
      <v-text-field
        v-if="creator"
        v-model.trim="form.description"
        label="Description"
        @keypress.enter="submitForm"
      />
      <ums-password-field
        v-if="creator"
        v-model="form.password"
        @keypress.enter="submitForm"
      />
      <v-text-field
        v-model.trim="form.tokenLifetime"
        label="Token lifetime"
        @keypress.enter="submitForm"
      >
        <template #append-outer>
          <v-tooltip top>
            <template #activator="{ on }">
              <v-btn
                icon
                small
                v-on="on"
              >
                <v-icon>
                  help
                </v-icon>
              </v-btn>
            </template>
            Format of lifetime 'hh:mm:ss' or 'd.hh:mm:ss'
          </v-tooltip>
        </template>
      </v-text-field>
      <strong>
        Login types
      </strong>
      <v-divider />
      <v-layout>
        <v-checkbox
          v-for="(type) in loginTypes"
          v-model="form.loginTypes"
          :rules="validator.getRulesByField('loginTypes')"
          :key="type"
          :value="type"
          :label="type"
          class="mr-2"
        />
      </v-layout>
      <strong>
        Azure App Body
      </strong>
      <v-divider />
      <v-layout class="mt-2">
        <vbt-code
          v-model.trim="azureAppBody"
          class="mt-3"
        />
      </v-layout>
      <span
        v-if="isInvalidJson"
        class="error--text"
      >
        Invalid JSON
      </span>
    </v-form>
  </vbt-dialog>
</template>

<script>
import { mapState } from 'vuex';

import { useDialog, useEditor, useFormWithRef } from '@mixins/factories';

import { Validator, wrapToLoadingFn, prepareJSONFromStr } from '@helpers';

import { namespace as EnumsNamespace } from '@store/modules/enums.types';

import ApplicationsApiService from '../_services/applications.api.service';

import { generateApplicationModel } from '../_models/application.model';

const validator = Validator.createValidator({
  name: ['required'],
  loginTypes: ['requiredArray'],
});

export default {
  name: 'UmsApplicationEditor',
  mixins: [
    useFormWithRef(),
    Validator.useValidator(validator),
    useDialog({ openHandlerName: 'setForm' }),
    useEditor({ formGenerator: generateApplicationModel }),
  ],
  data() {
    return {
      isInvalidJson: false,
    };
  },
  computed: {
    ...mapState(EnumsNamespace, {
      loginTypes: ({ enums: { serviceLoginType: items } }) => items || [],
    }),
    title() {
      return this.creator ? 'Create application' : `Edit ${this.item.name}`;
    },
    action() {
      return this.creator
        ? ApplicationsApiService.createApplication
        : ApplicationsApiService.updateApplication;
    },
    submitPromptText() {
      return this.creator ? 'Create application?' : `Update application ${this.item.name}`;
    },
    azureAppBody: {
      get() {
        return prepareJSONFromStr(this.form.azureAppBody);
      },
      set(newValue) {
        try {
          this.isInvalidJson = false;
          JSON.parse(newValue);
          this.$set(this.form, 'azureAppBody', newValue);
        } catch {
          this.isInvalidJson = true;
        }
      },
    },
  },
  methods: {
    wrapToLoadingFn,
    submitForm() {
      if (!this.$_validateForm()) {
        return;
      }

      this.$VBlackerTheme.alert.warning({
        text: this.submitPromptText,
      }, () => this.wrapToLoadingFn({
        req: this.action.bind({}, { name: this.item.name, id: this.form.id, data: this.form }),
        fullscreenLoadingSpinner: true,
        onSuccess: () => {
          this.$VBlackerTheme.notification.success(`Successfully ${
            this.creator ? 'created' : 'updated'
          }`);
          this.$emit('success');
          this.$_closeDialog();
        },
      }));
    },
  },
};
</script>
